import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './ProjectDetails.css';

interface QuotationData {
  text?: string;
  files?: Array<{
    name: string;
    url: string;
    type: string;
  }>;
}

interface Vendor {
  id: number;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  category: string;
  address?: string;
  rate?: string;
  notes?: string;
  quotation?: QuotationData;
  dailyStatuses?: DailyStatus[];
  furnitureItems?: string;
  furnitureSummaryFile?: string;
  projectNames?: string[];
}

interface DailyStatus {
  date: string;
  workDescription: string;
  images: string[];
}

interface FurnitureItem {
  id: number;
  itemNo: number;
  name: string;
  quantity: number;
  estimatedCost: string;
  status: 'Pending' | 'Ordered' | 'Delivered';
  image?: string;
}

interface VendorDetailsProps {
  vendorType: 'labour' | 'material';
}

const VendorDetails: React.FC<VendorDetailsProps> = ({ vendorType }) => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'quotation' | 'daily' | 'furniture'>('quotation');
  const [selectedProject, setSelectedProject] = useState<string>('');

  // Quotation state
  const [quotation, setQuotation] = useState<QuotationData>({ text: '', files: [] });
  const [isEditingQuotation, setIsEditingQuotation] = useState(false);

  // Daily Status state
  const [dailyStatuses, setDailyStatuses] = useState<DailyStatus[]>([]);
  const [newStatus, setNewStatus] = useState({
    date: new Date().toISOString().split('T')[0],
    workDescription: '',
    images: [] as string[]
  });
  const [isAddingStatus, setIsAddingStatus] = useState(false);

  // Image preview state
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Photo upload extras
  const [imageUrl, setImageUrl] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  // Furniture state (only for Interior Furniture vendors)
  const [furnitureItems, setFurnitureItems] = useState<FurnitureItem[]>([]);
  const [furnitureSummaryFile, setFurnitureSummaryFile] = useState<{ name: string; url: string; type: string } | null>(null);
  const [showFurnitureModal, setShowFurnitureModal] = useState(false);
  const [newFurniture, setNewFurniture] = useState<FurnitureItem>({
    id: 0,
    itemNo: 1,
    name: '',
    quantity: 1,
    estimatedCost: '',
    status: 'Pending',
    image: ''
  });

  // Client selection for fetching furniture items
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedFurnitureItem, setSelectedFurnitureItem] = useState<FurnitureItem | null>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const apiPath = vendorType === 'labour' ? '/labour-vendors' : '/material-vendors';
  const vendorLabel = vendorType === 'labour' ? 'Labour Vendor' : 'Material Vendor';

  const handleBackNavigation = () => {
    if (user?.role === 'vendor') {
      navigate('/dashboard');
    } else {
      navigate(`/${vendorType}-vendors`);
    }
  };

  useEffect(() => {
    fetchVendor();
  }, [vendorId]);

  const fetchVendor = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${apiPath}/${vendorId}`);

      // Parse project names from notes field
      const projectNames: string[] = [];
      if (response.data.notes && response.data.notes.trim()) {
        const names = response.data.notes.split('\n').filter((name: string) => name.trim());
        projectNames.push(...names.map((name: string) => name.trim()));
      }

      setVendor({ ...response.data, projectNames });
      setQuotation(response.data.quotation || { text: '', files: [] });
      setDailyStatuses(response.data.dailyStatuses || []);

      // Set first project as selected by default
      if (projectNames.length > 0 && !selectedProject) {
        setSelectedProject(projectNames[0]);
      }

      // Load furniture items for Interior Furniture vendors
      if (response.data.furnitureItems) {
        try {
          const items = typeof response.data.furnitureItems === 'string'
            ? JSON.parse(response.data.furnitureItems)
            : response.data.furnitureItems;
          setFurnitureItems(items || []);
        } catch (e) {
          setFurnitureItems([]);
        }
      }

      if (response.data.furnitureSummaryFile) {
        try {
          const file = typeof response.data.furnitureSummaryFile === 'string'
            ? JSON.parse(response.data.furnitureSummaryFile)
            : response.data.furnitureSummaryFile;
          setFurnitureSummaryFile(file);
        } catch (e) {
          setFurnitureSummaryFile(null);
        }
      }

      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load vendor details');
      console.error('Error fetching vendor:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuotationFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);
    const newFiles: Array<{ name: string; url: string; type: string }> = [];

    for (const file of fileArray) {
      if (file.type === 'application/pdf' ||
          file.type === 'application/vnd.ms-excel' ||
          file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {

        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = (event) => {
            if (event.target?.result) {
              newFiles.push({
                name: file.name,
                url: event.target.result as string,
                type: file.type
              });
            }
            resolve(null);
          };
          reader.readAsDataURL(file);
        });
      } else {
        alert('Only PDF and Excel files are allowed');
      }
    }

    setQuotation(prev => ({
      ...prev,
      files: [...(prev.files || []), ...newFiles]
    }));
  };

  const handleRemoveQuotationFile = (index: number) => {
    setQuotation(prev => ({
      ...prev,
      files: prev.files?.filter((_, i) => i !== index) || []
    }));
  };

  const handleSaveQuotation = async () => {
    try {
      await api.put(`${apiPath}/${vendorId}/quotation`, { quotation });
      setIsEditingQuotation(false);
      alert('Quotation saved successfully');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save quotation');
      console.error('Error saving quotation:', err);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();

      await new Promise((resolve) => {
        reader.onloadend = () => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 600;
            const MAX_HEIGHT = 600;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height = (height * MAX_WIDTH) / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width = (width * MAX_HEIGHT) / height;
                height = MAX_HEIGHT;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);

            const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.3);
            newImages.push(compressedDataUrl);
            resolve(null);
          };
          img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
      });
    }

    setNewStatus({ ...newStatus, images: [...newStatus.images, ...newImages] });
  };

  const handleRemoveImage = (index: number) => {
    setNewStatus({
      ...newStatus,
      images: newStatus.images.filter((_, i) => i !== index)
    });
  };

  const handleAddImageUrl = () => {
    const url = imageUrl.trim();
    if (!url) return;
    setNewStatus({ ...newStatus, images: [...newStatus.images, url] });
    setImageUrl('');
  };

  const compressAndAdd = (file: File): Promise<void> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX = 600;
          let w = img.width, h = img.height;
          if (w > h) { if (w > MAX) { h = (h * MAX) / w; w = MAX; } }
          else { if (h > MAX) { w = (w * MAX) / h; h = MAX; } }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d')?.drawImage(img, 0, 0, w, h);
          const data = canvas.toDataURL('image/jpeg', 0.3);
          setNewStatus(prev => ({ ...prev, images: [...prev.images, data] }));
          resolve();
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    for (const file of files) await compressAndAdd(file);
  };

  const handleAddDailyStatus = async () => {
    if (!newStatus.workDescription) {
      alert('Please enter work description');
      return;
    }

    try {
      const updatedStatuses = [...dailyStatuses, newStatus];
      await api.put(`${apiPath}/${vendorId}/daily-statuses`, { dailyStatuses: updatedStatuses });
      setDailyStatuses(updatedStatuses);
      setNewStatus({
        date: new Date().toISOString().split('T')[0],
        workDescription: '',
        images: []
      });
      setIsAddingStatus(false);
      alert('Daily status added successfully');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add daily status');
      console.error('Error adding daily status:', err);
    }
  };

  const handleDeleteStatus = async (index: number) => {
    if (!window.confirm('Are you sure you want to delete this daily status?')) {
      return;
    }

    try {
      const updatedStatuses = dailyStatuses.filter((_, i) => i !== index);
      await api.put(`${apiPath}/${vendorId}/daily-statuses`, { dailyStatuses: updatedStatuses });
      setDailyStatuses(updatedStatuses);
      alert('Daily status deleted successfully');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete daily status');
      console.error('Error deleting daily status:', err);
    }
  };

  const saveFurnitureItems = async (items: FurnitureItem[], summaryFile: { name: string; url: string; type: string } | null = furnitureSummaryFile) => {
    try {
      console.log('Saving furniture items:', items);
      console.log('API path:', `${apiPath}/${vendorId}/furniture-items`);
      const response = await api.put(`${apiPath}/${vendorId}/furniture-items`, {
        furnitureItems: items,
        furnitureSummaryFile: summaryFile
      });
      console.log('Save response:', response.data);
    } catch (err: any) {
      console.error('Error saving furniture items:', err);
      console.error('Error response:', err.response?.data);
      alert(err.response?.data?.message || 'Failed to save furniture items');
      throw err;
    }
  };

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const response = await api.get('/projects');
      console.log('Fetched clients/projects:', response.data);
      console.log('First client:', response.data[0]);
      setClients(response.data);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to load clients');
      console.error('Error fetching clients:', err);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleFetchToClient = async (clientId: number) => {
    if (!selectedFurnitureItem) return;

    try {
      console.log('Adding furniture to client:', clientId);
      console.log('Client ID type:', typeof clientId);
      console.log('Client ID value:', clientId);
      console.log('Selected furniture item:', selectedFurnitureItem);

      // Get the client's existing furniture items
      const url = `/projects/${clientId}`;
      console.log('Fetching client from URL:', url);
      const clientResponse = await api.get(url);
      console.log('Client data:', clientResponse.data);

      const existingFurniture = clientResponse.data.furnitureItems ?
        (typeof clientResponse.data.furnitureItems === 'string' ?
          JSON.parse(clientResponse.data.furnitureItems) :
          clientResponse.data.furnitureItems) : [];

      console.log('Existing furniture:', existingFurniture);

      // Add the new furniture item
      const newItem = {
        ...selectedFurnitureItem,
        id: Date.now(),
        itemNo: existingFurniture.length > 0 ? Math.max(...existingFurniture.map((i: any) => i.itemNo)) + 1 : 1
      };
      const updatedFurniture = [...existingFurniture, newItem];

      console.log('Updated furniture to save:', updatedFurniture);

      // Save to client project
      const response = await api.put(`/projects/${clientId}/furniture-items`, {
        furnitureItems: updatedFurniture
      });

      console.log('Save response:', response.data);

      alert(`Furniture item "${selectedFurnitureItem.name}" added to client project successfully!`);
      setShowClientModal(false);
      setSelectedFurnitureItem(null);
    } catch (err: any) {
      console.error('Full error object:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      alert(err.response?.data?.message || 'Failed to add furniture item to client');
      console.error('Error adding furniture to client:', err);
    }
  };

  if (loading) {
    return (
      <div className="project-details-container">
        <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#666' }}>
          Loading vendor details...
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="project-details-container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#f44336', fontSize: '16px', marginBottom: '20px' }}>{error || 'Vendor not found'}</p>
          <button
            onClick={handleBackNavigation}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Back to {vendorLabel}s
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-details-container">
      <header className="project-details-header">
        <div className="header-left">
          <button className="btn-back" onClick={handleBackNavigation} style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}>
            ← Back
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
          <h1>{vendor.name}</h1>
          <p className="project-location">
            {vendor.category} | {vendor.phone}
            {vendor.company && ` | ${vendor.company}`}
          </p>
          {vendor.projectNames && vendor.projectNames.length > 0 && (
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="project-select" style={{ fontSize: '14px', fontWeight: '600', color: '#666' }}>
                Project:
              </label>
              <select
                id="project-select"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                style={{
                  padding: '8px 16px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#fff',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  minWidth: '200px',
                  textAlign: 'center'
                }}
              >
                {vendor.projectNames.map((projectName, index) => (
                  <option key={index} value={projectName}>
                    {projectName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="project-tabs">
        <button
          className={`tab-btn ${activeTab === 'quotation' ? 'active' : ''}`}
          onClick={() => setActiveTab('quotation')}
        >
          Quotation
        </button>
        <button
          className={`tab-btn ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          Daily Status
        </button>
        {vendor?.category === 'Interior Furniture' && (
          <button
            className={`tab-btn ${activeTab === 'furniture' ? 'active' : ''}`}
            onClick={() => setActiveTab('furniture')}
          >
            🪑 Interior Furniture
          </button>
        )}
      </div>

      {/* Quotation Tab */}
      {activeTab === 'quotation' && (
        <div className="project-content">
          <div className="quotation-controls">
            <h2 style={{ margin: 0, color: '#333' }}>Quotation</h2>
            {!isEditingQuotation ? (
              <button className="btn-edit-quotation" onClick={() => setIsEditingQuotation(true)}>
                ✏️ Edit
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn-save-quotation" onClick={handleSaveQuotation}>
                  💾 Save
                </button>
                <button className="btn-cancel-edit" onClick={() => setIsEditingQuotation(false)}>
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="quotation-breakdown">
            <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>Text Quotation</h3>
            <textarea
              value={quotation.text || ''}
              onChange={(e) => setQuotation({ ...quotation, text: e.target.value })}
              disabled={!isEditingQuotation}
              placeholder="Enter quotation details, pricing, terms, etc."
              rows={8}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                background: isEditingQuotation ? 'white' : '#f9f9f9'
              }}
            />

            <div style={{ marginTop: '30px' }}>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>Quotation Files (PDF/Excel)</h3>

              {isEditingQuotation && (
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="file"
                    accept=".pdf,.xls,.xlsx"
                    multiple
                    onChange={handleQuotationFileUpload}
                    style={{
                      padding: '10px',
                      border: '2px dashed #667eea',
                      borderRadius: '8px',
                      width: '100%',
                      cursor: 'pointer',
                      background: '#f5f7fa'
                    }}
                  />
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Upload PDF or Excel files (multiple files allowed)
                  </p>
                </div>
              )}

              {quotation.files && quotation.files.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {quotation.files.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '15px',
                        background: '#f5f7fa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>
                          {file.type === 'application/pdf' ? '📄' : '📊'}
                        </span>
                        <div>
                          <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>{file.name}</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            {file.type === 'application/pdf' ? 'PDF Document' : 'Excel Spreadsheet'}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <a
                          href={file.url}
                          download={file.name}
                          style={{
                            padding: '8px 16px',
                            background: '#667eea',
                            color: 'white',
                            borderRadius: '6px',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          Download
                        </a>
                        {isEditingQuotation && (
                          <button
                            onClick={() => handleRemoveQuotationFile(index)}
                            style={{
                              padding: '8px 16px',
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                  No files uploaded yet
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Daily Status Tab */}
      {activeTab === 'daily' && (
        <div className="project-content">
          <div className="daily-status-section">
            <button className="btn-add-status" onClick={() => setIsAddingStatus(!isAddingStatus)}>
              {isAddingStatus ? '✖ Cancel' : '+ Add Status'}
            </button>

          {isAddingStatus && (
            <div className="daily-status-form" style={{ marginBottom: '20px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={newStatus.date}
                  onChange={(e) => setNewStatus({ ...newStatus, date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div className="form-group">
                <label>Work Description</label>
                <textarea
                  value={newStatus.workDescription}
                  onChange={(e) => setNewStatus({ ...newStatus, workDescription: e.target.value })}
                  placeholder="Describe the work done today..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div className="form-group">
                <label>Work Photos (Optional)</label>

                {/* Option 1: Paste URL */}
                <div className="photo-url-row">
                  <input
                    type="text"
                    className="photo-url-input"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Paste image URL here..."
                    onKeyDown={(e) => e.key === 'Enter' && handleAddImageUrl()}
                  />
                  <button type="button" className="btn-add-url" onClick={handleAddImageUrl}>
                    Add URL
                  </button>
                </div>

                {/* Option 2: Drag & Drop */}
                <div
                  className={`drop-zone${isDragOver ? ' drag-over' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                >
                  <p>📂 Drag & drop photos here</p>
                </div>

                {/* Option 3: Local file browse */}
                <label className="btn-browse-files">
                  📁 Browse from Computer
                  <input type="file" accept="image/*" multiple onChange={handleImageUpload} style={{ display: 'none' }} />
                </label>
                {newStatus.images.length > 0 && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                    {newStatus.images.map((img, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img src={img} alt={`Upload ${index + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px' }} />
                        <button
                          onClick={() => handleRemoveImage(index)}
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleAddDailyStatus}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Add Daily Status
              </button>
            </div>
          )}

          {/* Display Daily Statuses */}
          <div className="status-timeline">
            {dailyStatuses.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>No daily status updates yet</p>
            ) : (
              dailyStatuses.slice().reverse().map((status, index) => (
                <div key={index} className="status-card">
                  <div className="status-header">
                    <h3>{new Date(status.date).toLocaleDateString()}</h3>
                    <button
                      className="btn-delete-status"
                      onClick={() => handleDeleteStatus(dailyStatuses.length - 1 - index)}
                    >
                      🗑️
                    </button>
                  </div>
                  <p className="status-description">{status.workDescription}</p>
                  {status.images && status.images.length > 0 && (
                    <div className="status-images">
                      {status.images.map((img, imgIndex) => (
                        <img
                          key={imgIndex}
                          src={img}
                          alt={`Status ${imgIndex + 1}`}
                          className="status-image"
                          onClick={() => setPreviewImage(img)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          </div>
        </div>
      )}

      {/* Furniture Tab - Only for Interior Furniture vendors */}
      {activeTab === 'furniture' && vendor?.category === 'Interior Furniture' && (
        <div className="project-content">
          <div className="furniture-section">
            <button className="btn-add-furniture" onClick={() => {
              const maxItemNo = furnitureItems.length > 0 ? Math.max(...furnitureItems.map(i => i.itemNo)) : 0;
              setNewFurniture({ id: 0, itemNo: maxItemNo + 1, name: '', quantity: 1, estimatedCost: '', status: 'Pending', image: '' });
              setShowFurnitureModal(true);
            }}>+ Add Furniture Item</button>

            <div className="furniture-table">
              <table>
                <thead>
                  <tr>
                    <th>Item No</th>
                    <th>Picture</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Estimated Cost</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {furnitureItems.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        No furniture items added yet
                      </td>
                    </tr>
                  ) : (
                    furnitureItems.map((item) => (
                      <tr key={item.id}>
                        <td>{item.itemNo}</td>
                        <td>
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                              onClick={() => setPreviewImage(item.image || '')}
                              title="Click to view full size"
                            />
                          ) : (
                            <div style={{
                              width: '60px',
                              height: '60px',
                              background: '#f0f0f0',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '24px'
                            }}>
                              📷
                            </div>
                          )}
                        </td>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                        <td>{item.estimatedCost}</td>
                        <td>
                          <span
                            className="furniture-status-badge"
                            style={{
                              backgroundColor:
                                item.status === 'Delivered' ? '#66BB6A' :
                                item.status === 'Ordered' ? '#42A5F5' : '#FFA726'
                            }}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              className="btn-edit-status"
                              onClick={() => {
                                setSelectedFurnitureItem(item);
                                fetchClients();
                                setShowClientModal(true);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}
                              title="Add to Client Project"
                            >
                              ➕ Add
                            </button>
                            <button
                              className="btn-edit-status"
                              onClick={() => {
                                setNewFurniture(item);
                                setShowFurnitureModal(true);
                              }}
                              title="Edit furniture item"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-delete-status"
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to delete this furniture item?')) {
                                  const updatedItems = furnitureItems.filter(f => f.id !== item.id);
                                  try {
                                    await saveFurnitureItems(updatedItems);
                                    setFurnitureItems(updatedItems);
                                    alert('Furniture item deleted successfully');
                                  } catch (err) {
                                    // Error already handled in saveFurnitureItems
                                  }
                                }
                              }}
                              title="Delete furniture item"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Furniture Summary File Upload */}
            <div style={{
              marginTop: '30px',
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                📋 Furniture Items Summary
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <input
                  type="file"
                  accept=".pdf,.xls,.xlsx"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = async () => {
                        const newSummaryFile = {
                          name: file.name,
                          url: reader.result as string,
                          type: file.type
                        };
                        try {
                          await saveFurnitureItems(furnitureItems, newSummaryFile);
                          setFurnitureSummaryFile(newSummaryFile);
                          alert('Summary file uploaded successfully');
                        } catch (err) {
                          // Error already handled in saveFurnitureItems
                        }
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  style={{ display: 'none' }}
                  id="furniture-summary-upload"
                />
                <label
                  htmlFor="furniture-summary-upload"
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: 'none'
                  }}
                >
                  📎 Upload Summary (PDF/Excel)
                </label>
                {furnitureSummaryFile && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 15px',
                    background: 'white',
                    borderRadius: '6px',
                    border: '1px solid #ddd'
                  }}>
                    <span style={{ fontSize: '20px' }}>
                      {furnitureSummaryFile.type.includes('pdf') ? '📕' : '📗'}
                    </span>
                    <span style={{ fontSize: '14px', color: '#333' }}>{furnitureSummaryFile.name}</span>
                    <a
                      href={furnitureSummaryFile.url}
                      download={furnitureSummaryFile.name}
                      style={{
                        padding: '6px 12px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Download
                    </a>
                    <button
                      type="button"
                      onClick={() => setFurnitureSummaryFile(null)}
                      style={{
                        padding: '6px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <p style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
                Upload the complete furniture items summary document (PDF or Excel format)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Furniture Modal */}
      {showFurnitureModal && (
        <div className="modal-overlay" onClick={() => setShowFurnitureModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{newFurniture.id === 0 || newFurniture.id > Date.now() - 1000 ? 'Add Furniture Item' : 'Edit Furniture Item'}</h2>
              <button className="btn-close" onClick={() => setShowFurnitureModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  value={newFurniture.name}
                  onChange={(e) => setNewFurniture({ ...newFurniture, name: e.target.value })}
                  placeholder="e.g., Dining Table, Sofa Set"
                />
              </div>
              <div className="form-group">
                <label>Quantity *</label>
                <input
                  type="number"
                  min="1"
                  value={newFurniture.quantity}
                  onChange={(e) => setNewFurniture({ ...newFurniture, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="form-group">
                <label>Estimated Cost *</label>
                <input
                  type="text"
                  value={newFurniture.estimatedCost}
                  onChange={(e) => setNewFurniture({ ...newFurniture, estimatedCost: e.target.value })}
                  placeholder="e.g., 2500"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={newFurniture.status}
                  onChange={(e) => setNewFurniture({ ...newFurniture, status: e.target.value as 'Pending' | 'Ordered' | 'Delivered' })}
                >
                  <option value="Pending">Pending</option>
                  <option value="Ordered">Ordered</option>
                  <option value="Delivered">Delivered</option>
                </select>
              </div>
              <div className="form-group">
                <label>Picture (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        setNewFurniture({ ...newFurniture, image: reader.result as string });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                {newFurniture.image && (
                  <div style={{ marginTop: '10px' }}>
                    <img
                      src={newFurniture.image}
                      alt="Preview"
                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px' }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowFurnitureModal(false)}>
                Cancel
              </button>
              <button
                className="btn-submit"
                onClick={async () => {
                  console.log('Current newFurniture state:', newFurniture);
                  console.log('Current furnitureItems state:', furnitureItems);

                  if (!newFurniture.name || !newFurniture.estimatedCost) {
                    alert('Please fill in all required fields');
                    return;
                  }

                  let updatedItems: FurnitureItem[];
                  if (newFurniture.id === 0 || newFurniture.id > Date.now() - 1000) {
                    // Add new
                    updatedItems = [...furnitureItems, { ...newFurniture, id: Date.now() }];
                  } else {
                    // Edit existing
                    updatedItems = furnitureItems.map(item =>
                      item.id === newFurniture.id ? newFurniture : item
                    );
                  }

                  console.log('Updated items to save:', updatedItems);

                  try {
                    await saveFurnitureItems(updatedItems);
                    setFurnitureItems(updatedItems);
                    setShowFurnitureModal(false);
                    setNewFurniture({ id: 0, itemNo: 1, name: '', quantity: 1, estimatedCost: '', status: 'Pending', image: '' });
                    alert(newFurniture.id === 0 || newFurniture.id > Date.now() - 1000 ? 'Furniture item added successfully' : 'Furniture item updated successfully');
                  } catch (err) {
                    // Error already handled in saveFurnitureItems
                  }
                }}
              >
                {newFurniture.id === 0 || newFurniture.id > Date.now() - 1000 ? 'Add' : 'Update'} Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Selection Modal */}
      {showClientModal && (
        <div className="modal-overlay" onClick={() => setShowClientModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Select Client Project</h2>
              <button className="btn-close-modal" onClick={() => setShowClientModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Select a client project to add "{selectedFurnitureItem?.name}" to their furniture list:
              </p>
              {loadingClients ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>Loading clients...</div>
              ) : clients.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>No client projects found</div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {clients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => handleFetchToClient(client.id)}
                      style={{
                        padding: '15px',
                        margin: '10px 0',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: 'white'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.background = '#f8f9ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <div style={{ fontWeight: '600', fontSize: '16px' }}>
                        {client.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowClientModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#333',
              fontWeight: 'bold',
              zIndex: 2001
            }}
          >
            ×
          </button>
          <img
            src={previewImage}
            alt="Preview"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default VendorDetails;
